import { AnimatePresence, m, useSpring } from "framer-motion";
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KartRun } from "../../../api/kart-api";
import { hapticTick } from "../../../haptics";
import { isDebugOn } from "./dev";
import { EASE } from "../Motion";
import KartMenu, { MenuPage, Mode, MenuRecord } from "./KartMenu";
import { Chunky, FlagIcon, GhostIcon, HUD_OUTLINE, ItemIcon, SpeakerIcon, Face, Stars, TrophyIcon, formatTime, rgbCss } from "./KartUi";
import { CharKey, ROSTER_KEYS, characterOf, modelFailures, sprite } from "./models";
import { ambience, engineAt, hold, isMuted, music, outro, setMuted, sfx, silence, unlock } from "./audio";
import { GHOST_STEP, KartRenderer, createKartRenderer, kartStyle } from "./renderer";
import { Sectors, follow, newSectors, note, saved } from "./sectors";
import {
  COUNTDOWN,
  MAX_SPEED,
  DIFFICULTIES,
  Assists,
  BASS_RADIUS,
  Difficulty,
  ITEMS,
  ItemKind,
  Race,
  RaceEvent,
  Racer,
  SMOKE_RADIUS,
  STEP,
  Shot,
  backness,
  createRace,
  isPuddle,
  raceTime,
  rubberBand,
  starsFor,
  step,
} from "./sim";
import { MAPS, SWARM_RADIUS, Track, VARIANTS, Variant, buildTrack, hazardLat, roadY, trackId, variantOf, weeklyChallenge } from "./tracks";
import { trackOverride } from "./segments";
import { TICK_STEP, TIER_COLORS, tickSamples, tierSpans, whereText } from "./trackDebug";
import { TUNING, applyTuningOverride } from "./tuning";

// The kart game as a component: a canvas, the race in a ref, and one loop
// stepping it. React only hears about what the HUD shows — place, lap, item,
// countdown — and only when one of them changes.

type Screen = "menu" | "race" | "results";

interface Hud {
  place: number;
  lap: number;
  laps: number;
  /** Whether the rival is ahead of you, or null with no rival. */
  rivalAhead: boolean | null;
  item: ItemKind | null;
  /** Zobbe's: the items waiting behind it, next first. */
  queue: ItemKind[];
  /** The item's still spinning in the slot, from a box. */
  rolling: boolean;
  /** In a jackpot frenzy, the reels stopped. */
  frenzy: boolean;
  count: number;
}

/** How the race went, beyond the places: everyone's time — guessed for those still driving — and your laps. */
interface Finish {
  times: Map<number, { time: number; guess: boolean }>;
  laps: number[];
  rival: number | null;
  score: number;
  by: Record<string, number>;
  bestChain: number;
  stars: number;
  /** Your best here before this race, or null if it's your first. */
  before: number | null;
  /** Whether it counts for the records: no help, and not training. */
  counts: boolean;
  training: boolean;
}

/** Only a race against the field, driven without help, counts for the records, your best and the ghost. */
const countsFor = (race: Race) => !race.training && !race.autopilot && !helpOn(race.assists);

/** What each kind of points is called, on a popup and in the results. */
const SCORE_LABELS: Record<string, string> = {
  drift: "Drift",
  trick: "Trick",
  hit: "Træf",
  box: "Kasse",
  pad: "Boost",
  pass: "Overhaling",
  slip: "Slipstream",
  finish: "Placering",
  rival: "Slog rivalen",
};

/**
 * The help you've chosen, kept on the phone: none to begin with. (A new key:
 * the old one held steering help on by default, and that mustn't carry over.)
 * A race with any help doesn't count for the records.
 */
const ASSISTS_KEY = "kartAssists2";
const NO_HELP: Assists = { steer: false, drift: false, item: false };
const loadAssists = (): Assists => {
  try {
    return { ...NO_HELP, ...JSON.parse(localStorage.getItem(ASSISTS_KEY) ?? "{}") };
  } catch {
    return NO_HELP;
  }
};
const helpOn = (a: Assists) => a.steer || a.drift || a.item;

/**
 * What a race is saved with, beyond its score: the numbers the game is
 * balanced by. Counted from the race's events as it's driven.
 */
interface Tally {
  drifts: number[];
  tricks: number;
  jumps: number;
  dealt: number;
  taken: number;
  rocketHits: number;
  items: Record<string, number>;
  frenzy: number;
  slips: number;
  start: "none" | "good" | "early";
  frames: number;
  ms: number;
  saved: boolean;
  /** Where on the lap the time went (sectors.ts). */
  sectors: Sectors;
}
const newTally = (): Tally => ({
  drifts: [0, 0, 0], tricks: 0, jumps: 0, dealt: 0, taken: 0, rocketHits: 0, items: {}, frenzy: 0, slips: 0, start: "none", frames: 0, ms: 0, saved: false,
  sectors: newSectors(),
});

/** Adds what just happened to the player's tally. */
function count(t: Tally, e: RaceEvent, player: number) {
  const mine = e.kart === player;
  if (e.kind === "drift" && mine) t.drifts[e.tier - 1]++;
  else if (e.kind === "trick" && mine) t.tricks++;
  else if (e.kind === "land" && mine) t.jumps++;
  else if (e.kind === "slip" && mine) t.slips++;
  else if (e.kind === "start" && mine) t.start = e.good ? "good" : "early";
  else if (e.kind === "box" && mine) {
    t.items[e.item] = (t.items[e.item] ?? 0) + 1;
    if (e.frenzy) t.frenzy++;
  } else if (e.kind === "hit") {
    if (e.by === player && !mine) t.dealt++;
    if (mine && e.spin) t.taken++;
    if (mine && e.what === "rocket") t.rocketHits++;
  }
}

/** A race as it's saved: finished, or quit (`abandoned`). */
/** A ghost to race: whose run it was, how fast, and its line (x, y, z, heading every GHOST_STEP). */
export interface Ghost {
  time: number;
  name: string;
  emoji: string;
  path: number[];
}

/** Your fastest run on each track, kept on the phone as a ghost to race. */
const GHOST_KEY = (track: string) => `kartGhost:${track}`;
const loadGhostHere = (track: string): Ghost | null => {
  try {
    return JSON.parse(localStorage.getItem(GHOST_KEY(track)) ?? "null");
  } catch {
    return null;
  }
};
const saveGhostHere = (track: string, ghost: Ghost) => {
  try {
    localStorage.setItem(GHOST_KEY(track), JSON.stringify(ghost));
  } catch {
    // Too big, or no storage: no ghost kept, then.
  }
};

function runOf(race: Race, t: Tally, splits: number[], abandoned: boolean, backend: string, ghost?: number[]): Omit<KartRun, "memberId"> {
  const player = race.karts.find((k) => k.player)!;
  const rival = race.karts.find((k) => k.id === race.rival);
  return {
    track: trackId(race.track.map),
    difficulty: race.difficulty,
    score: race.score.total,
    stars: starsFor(race.score.total, race.track.map.stars),
    place: player.place,
    raceTime: player.finishTime,
    abandoned,
    summary: {
      laps: splits.map((at, i) => +(at - (i ? splits[i - 1] : 0)).toFixed(2)),
      by: race.score.by,
      bestChain: race.score.best,
      beatRival: rival ? rival.place > player.place : null,
      drifts: t.drifts,
      tricks: t.tricks,
      jumps: t.jumps,
      hitsDealt: t.dealt,
      hitsTaken: t.taken,
      rocketHits: t.rocketHits,
      items: t.items,
      frenzy: t.frenzy,
      slips: t.slips,
      start: t.start,
      sectors: saved(t.sectors, race.karts.find((k) => k.player)?.finishTime ?? null),
      assists: { ...race.assists },
      fps: t.frames ? Math.round((1000 * t.frames) / t.ms) : null,
      backend,
      // The run's line, when it's the lodge's fastest here: the next ghost.
      ...(ghost ? { ghost } : {}),
    },
  };
}

/**
 * The first-time hints, and how often each has been shown on this phone:
 * three times at most, and never again once you've done the thing.
 */
const HINTS = {
  drift: "Hold helt ude gennem svinget for at drifte — slip for boost",
  trick: "Svirp op for et trick!",
  item: "Tryk for at skyde — også med en finger mere · svirp ned: bagud",
};
type Hint = keyof typeof HINTS;
const HINTS_KEY = "kartHints";
const PILLS_KEY = "kartPills";
const loadHints = (): Record<Hint, number> => {
  try {
    return { drift: 0, trick: 0, item: 0, ...JSON.parse(localStorage.getItem(HINTS_KEY) ?? "{}") };
  } catch {
    return { drift: 0, trick: 0, item: 0 };
  }
};

/** Your best score for each track and difficulty, on this device. */
const BEST_KEY = "kartBest";
const loadBest = (): Record<string, number> => {
  try {
    return JSON.parse(localStorage.getItem(BEST_KEY) ?? "{}");
  } catch {
    return {};
  }
};
const saveBest = (best: Record<string, number>) => {
  try {
    localStorage.setItem(BEST_KEY, JSON.stringify(best));
  } catch {
    // No storage: the best just isn't kept.
  }
};

/** Pixels of sideways drag for full lock, by how sensitive you want the steering; kept on the phone. */
const STEER_DRAGS = { high: 45, normal: 60, low: 80 };
type Sensitivity = keyof typeof STEER_DRAGS;
const SENSITIVITY_KEY = "kartSensitivity";
const loadSensitivity = (): Sensitivity => {
  try {
    const saved = localStorage.getItem(SENSITIVITY_KEY);
    return saved && saved in STEER_DRAGS ? (saved as Sensitivity) : "normal";
  } catch {
    return "normal";
  }
};
/**
 * Pixels of drag that do nothing: a finger's contact point rolls a few pixels
 * as it lands, and without this the kart twitched — swerve, lean, camera
 * swing — the instant it touched the glass.
 */
const STEER_DEAD = 8;

/** The top of the game frame — place, item, ✕ — that stays React's to touch. */
const HUD_STRIP = 96;

/**
 * Render resolutions to step between, sharpest first. Frames that keep
 * missing a 60 Hz refresh drop a step — so a phone that can't keep up gets
 * smooth motion before sharpness. Only ever down: on a screen locked to its
 * refresh, a frame that took 2 ms and one that took 16 both arrive 16.7 ms
 * apart, so "easy" frames prove nothing, and climbing back up on them used
 * to see-saw — each step a canvas resize, and each resize a visible jolt.
 */
const RESOLUTIONS = [2, 1.75, 1.5, 1.25, 1];

/** Someone who's asked for less motion: no speed lines. The camera keeps still too (renderer.ts). */
const CALM = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/** What comes for you, on the warning at the bottom of the screen: each drawn there, one shown at a time. */
const THREATS = ["glove", "can", "mosquito", "rocket"] as const;

const QUIET = { smoke: 0, mosquito: 0, techno: 0 };

/**
 * How loud each of the weapons' ongoing sounds is for the player, 0 to 1 by
 * how far off it is: a smoke machine or Soundboks on anyone's kart, mosquito
 * swarms on the road or hunting someone.
 */
function earshot(race: Race, player: Race["karts"][number]) {
  const near = (d: number, reach: number) => Math.max(0, 1 - d / reach);
  const level = { smoke: 0, mosquito: player.itch > 0 ? 1 : 0, techno: 0 };
  for (const k of race.karts) {
    const d = Math.hypot(k.x - player.x, k.z - player.z);
    if (k.smoke > 0) level.smoke = Math.max(level.smoke, near(d, SMOKE_RADIUS * 3));
    if (k.bass > 0) level.techno = Math.max(level.techno, near(d, BASS_RADIUS * 4));
  }
  for (const h of race.track.hazards) {
    if (h.kind !== "swarm") continue;
    const s = race.track.samples[h.idx];
    const lat = hazardLat(h, race.time);
    level.mosquito = Math.max(level.mosquito, near(Math.hypot(s.x + s.nx * lat - player.x, s.z + s.nz * lat - player.z), SWARM_RADIUS * 6));
  }
  for (const shot of race.shots) {
    if (shot.kind === "mosquito") level.mosquito = Math.max(level.mosquito, near(Math.hypot(shot.x - player.x, shot.z - player.z), 32));
  }
  return level;
}

/**
 * The nearest shot coming for the player that they can't see: a swarm hunting
 * them, or a glove or can closing from behind. Where it is — -1 hard left to
 * 1 hard right — and how close, 0 (far) to 1 (on you).
 */
function threatTo(race: Race, player: Race["karts"][number]) {
  const fx = Math.cos(player.heading);
  const fz = Math.sin(player.heading);
  let best: { shot: Shot; side: number; near: number } | null = null;
  for (const shot of race.shots) {
    if (shot.owner === player.id) continue;
    const homing = (shot.kind === "mosquito" || shot.kind === "rocket") && shot.target === player.id;
    if (!homing && shot.kind !== "glove" && shot.kind !== "can") continue;
    const dx = shot.x - player.x;
    const dz = shot.z - player.z;
    const d = Math.hypot(dx, dz);
    // A swarm hunts from further off, a rocket from further still — time to get
    // the Soundboks going; a glove or can only matters from behind, coming closer.
    const range = TUNING.juice.warnRange * (shot.kind === "rocket" ? 4 : homing ? 2 : 1);
    const behind = dx * fx + dz * fz < 0;
    const closing = dx * (shot.vx - Math.cos(player.heading) * player.speed) + dz * (shot.vz - Math.sin(player.heading) * player.speed) < 0;
    if (d > range || (!homing && !(behind && closing))) continue;
    const near = 1 - d / range;
    if (!best || near > best.near) best = { shot, side: d > 0 ? (dz * fx - dx * fz) / d : 0, near };
  }
  return best;
}

/**
 * iPhones and iPads (every browser there is Safari underneath). Their WebGPU
 * drops frames whenever a finger lands — far worse than their WebGL — and
 * pressing is how the game is steered, so they draw with WebGL.
 */
const IOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

/**
 * The character you drive: the one you are. A member outside the roster of
 * seven gets one by their id, the same every time.
 */
const charOf = (member: Racer): CharKey => characterOf(member) ?? ROSTER_KEYS[Math.abs(member.id) % ROSTER_KEYS.length];

export default function KartRace({
  me: member,
  opponents: roster,
  onExit,
  onRun,
  records = [],
  loadGhost,
  week = [],
}: {
  /** The member playing: they drive the roster character they are. */
  me: Racer;
  /** The roster, one racer per character: whichever six you aren't driving make up the field. */
  opponents: Racer[];
  onExit?: () => void;
  /** A race over — finished or quit — to be saved. */
  onRun?: (run: Omit<KartRun, "memberId">) => void;
  /** The lodge's records: each member's top score and fastest time per track and difficulty, from races driven without help. */
  records?: MenuRecord[];
  /** The lodge's fastest run on a track, with its line, to race as a ghost. */
  loadGhost?: (track: string) => Promise<Ghost | null>;
  /** This week's challenge: each member's best so far, best first. */
  week?: { memberId: number; name: string; emoji: string; score: number; time: number | null }[];
}) {
  const me = useMemo(() => ({ ...member, char: charOf(member) }), [member]);
  // The other six.
  const opponents = useMemo(() => roster.filter((r) => r.char !== me.char), [roster, me.char]);
  const meRef = useRef(me);
  meRef.current = me;
  /** The ghost in this race; and the lodge's fastest time here, or null if none's known. */
  const ghost = useRef<Ghost | null>(null);
  const lodgeTime = useRef<number | null>(null);
  const [ghostShown, setGhostShown] = useState<Ghost | null>(null);
  /** Your line this race, for a ghost of it: x, y, z, heading, every GHOST_STEP. */
  const line = useRef<number[]>([]);
  const lineAt = useRef(0);
  const challenge = useMemo(() => weeklyChallenge(), []);
  const onRunRef = useRef(onRun);
  onRunRef.current = onRun;
  const tally = useRef(newTally());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const miniRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const knobRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<KartRenderer | null>(null);
  const raceRef = useRef<Race | null>(null);
  const input = useRef({ steer: 0, fire: false, trick: false, back: false, touch: false, keys: new Set<string>() });
  const statsRef = useRef<HTMLDivElement>(null);
  /** The box's size, and which of RESOLUTIONS we're drawing at. */
  const view = useRef({ width: 0, height: 0, level: 0 });
  const applySize = useRef(() => {
    const { width, height, level } = view.current;
    if (width > 8 && height > 8) {
      rendererRef.current?.resize(width, height, levels()[level]);
      probe.current.resizes++;
    }
  });
  /**
   * Debug mode's press probe: what the frames did in the moment after a finger
   * went down — the slowest one, how late the touch reached us, and whether
   * the canvas was resized — against the slowest frame of the second before.
   */
  const probe = useRef({ at: 0, lag: 0, moveLag: 0, worst: 0, js: 0, resizes: 0, resizesAtPress: 0, report: "" });

  const [screen, setScreen] = useState<Screen>("menu");
  const [mapKey, setMapKey] = useState(MAPS[0].key);
  const [variant, setVariant] = useState<Variant>("normal");
  const [page, setPage] = useState<MenuPage>("home");
  const [mode, setMode] = useState<Mode>("race");
  const [difficulty, setDifficulty] = useState<Difficulty>("medium");
  const [failed, setFailed] = useState(false);
  const [hud, setHud] = useState<Hud>({ place: 0, lap: 1, laps: TUNING.laps, rivalAhead: null, item: null, queue: [], rolling: false, frenzy: false, count: COUNTDOWN });
  /** The three reels of the last box: what they land on, and whether it's the jackpot. */
  const [reels, setReels] = useState<{ key: number; faces: ItemKind[] } | null>(null);
  const [results, setResults] = useState<Race["karts"]>([]);
  const [finish, setFinish] = useState<Finish>({ times: new Map(), laps: [], rival: null, score: 0, by: {}, bestChain: 0, stars: 0, before: null, counts: true, training: false });
  const [best, setBest] = useState(loadBest);
  const [assists, setAssists] = useState(loadAssists);
  const toggleAssist = (key: keyof Assists) => {
    const next = { ...assists, [key]: !assists[key] };
    setAssists(next);
    try {
      localStorage.setItem(ASSISTS_KEY, JSON.stringify(next));
    } catch {
      // Not kept, then.
    }
  };
  /** A word across the screen for a moment: the last lap, a start boost. */
  const [banner, setBanner] = useState<{ key: number; text: string } | null>(null);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const pause = useCallback((on: boolean) => {
    pausedRef.current = on;
    input.current.steer = 0;
    hold(on);
    setPaused(on);
  }, []);
  const [mute, setMute] = useState(isMuted);
  const toggleMute = () => {
    unlock();
    setMuted(!mute);
    setMute(!mute);
  };
  const screenRef = useRef(screen);
  screenRef.current = screen;
  /** Your time into each lap you've finished, off the race clock. */
  const splits = useRef<number[]>([]);
  /** A race quit before its end: saved as such, if it had got going. */
  const abandon = () => {
    const race = raceRef.current;
    if (!race || !countsFor(race) || race.phase === "countdown" || tally.current.saved) return;
    tally.current.saved = true;
    onRunRef.current?.(runOf(race, tally.current, splits.current, true, rendererRef.current?.kind ?? ""));
  };
  const timerRef = useRef<HTMLDivElement>(null);
  const linesRef = useRef<HTMLDivElement>(null);
  const vignetteRef = useRef<HTMLDivElement>(null);
  const flashRef = useRef<HTMLDivElement>(null);
  const warnRef = useRef<HTMLDivElement>(null);
  const scoreRef = useRef<HTMLDivElement>(null);
  const comboRef = useRef<HTMLDivElement>(null);
  const comboBarRef = useRef<HTMLDivElement>(null);
  const popsRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const joyRef = useRef<HTMLDivElement>(null);
  /** The how-to-drive pills: the first race of a session only. */
  const [pillsShown, setPillsShown] = useState(true);
  const wrongRef = useRef<HTMLDivElement>(null);
  const [sensitivity, setSensitivity] = useState(loadSensitivity);
  const steerDrag = useRef(STEER_DRAGS[sensitivity]);
  steerDrag.current = STEER_DRAGS[sensitivity];
  const [failures, setFailures] = useState(0);
  const racers = useMemo(() => [me, ...opponents], [me, opponents]);
  const map = useMemo(() => variantOf(MAPS.find((m) => m.key === mapKey)!, variant), [mapKey, variant]);
  const track = useMemo(() => buildTrack(map), [map]);

  const startRace = useCallback(
    (demo: boolean, training = false) => {
      if (isDebugOn()) applyTuningOverride();
      // In debug mode, a map saved as `kartTrack` is raced over the chosen one.
      const raced = isDebugOn() ? buildTrack(trackOverride(map)) : track;
      // The rival is drawn at random; in training there's no field at all.
      const race = createRace(raced, racers, me.id, difficulty, demo ? { laps: Infinity, autopilot: true } : { assists, training });
      // The demo lap behind the menu skips the countdown.
      if (demo) race.time = COUNTDOWN;
      raceRef.current = race;
      rendererRef.current?.setRace(race);
      // A ghost to race, in training only: this phone's fastest here, or the lodge's if that's faster.
      const id = trackId(track.map);
      const here = training ? loadGhostHere(id) : null;
      ghost.current = here;
      lodgeTime.current = null;
      rendererRef.current?.setGhost(here?.path ?? null);
      setGhostShown(here);
      line.current = [];
      lineAt.current = 0;
      if (!demo) {
        // The lodge's fastest is fetched for every race — to know whether yours beats it — but only raced against in training.
        loadGhost?.(id)
          .then((lodge) => {
            if (raceRef.current !== race || !lodge) return;
            lodgeTime.current = lodge.time;
            if (training && (!ghost.current || lodge.time < ghost.current.time)) {
              ghost.current = lodge;
              rendererRef.current?.setGhost(lodge.path);
              setGhostShown(lodge);
            }
          })
          .catch(() => {});
      }
      input.current.steer = 0;
      input.current.fire = false;
      splits.current = [];
      tally.current = newTally();
      // The engine and weapons are quiet until the lights go; the map's song starts as you enter it, and the menu's plays behind the menu.
      silence(demo);
      music(true, 1, demo ? "menu" : track.map.key);
      setBanner(null);
      if (!demo) {
        try {
          setPillsShown(!sessionStorage.getItem(PILLS_KEY));
          sessionStorage.setItem(PILLS_KEY, "1");
        } catch {
          setPillsShown(true);
        }
      }
      setScreen(demo ? "menu" : "race");
    },
    [track, map, racers, me.id, difficulty, assists, loadGhost],
  );

  // The renderer, once — WebGPU if the device has it, WebGL if not, which
  // takes a moment to find out. A new race hands it new karts and a new world.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let disposed = false;
    let renderer: KartRenderer | null = null;
    view.current.level = 0;
    createKartRenderer(canvas, IOS)
      .catch(() => null)
      .then((r) => {
        if (disposed) return r?.destroy();
        if (!r) return setFailed(true);
        renderer = rendererRef.current = r;
        applySize.current();
        if (raceRef.current) r.setRace(raceRef.current);
      });
    const observer = new ResizeObserver(([entry]) => {
      view.current.width = entry.contentRect.width;
      view.current.height = entry.contentRect.height;
      applySize.current();
    });
    observer.observe(containerRef.current!);
    return () => {
      disposed = true;
      observer.disconnect();
      renderer?.destroy();
      rendererRef.current = null;
    };
  }, []);

  /** Set when the next map has been picked to race straight away, rather than to show behind the menu. */
  const nextRace = useRef(false);
  const nextMap = MAPS[(MAPS.findIndex((m) => m.key === mapKey) + 1) % MAPS.length];

  // A demo race behind the menu, redone whenever the map changes.
  useEffect(() => {
    // "Næste bane" from the results: the new map is in, so its race starts now, in the same mode and variant.
    if (nextRace.current) {
      nextRace.current = false;
      unlock();
      startRace(false, mode === "training");
    } else if (screen === "menu") startRace(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track]);

  // The menu's music waits for a first tap: a browser won't make sound before one.
  useEffect(() => {
    if (screen !== "menu") return;
    const wake = () => unlock();
    window.addEventListener("pointerup", wake, { once: true });
    window.addEventListener("keydown", wake, { once: true });
    return () => {
      window.removeEventListener("pointerup", wake);
      window.removeEventListener("keydown", wake);
    };
  }, [screen]);

  // The loop.
  useEffect(() => {
    let frame = 0;
    let last = performance.now();
    let carry = 0;
    let doneAt = 0;
    const shown: Hud = { place: 0, lap: 0, laps: 0, rivalAhead: null, item: null, queue: [], rolling: false, frenzy: false, count: -1 };
    let popAt = 0;
    // The hints: how often each's been shown, which this race, and when the last went up.
    const hints = loadHints();
    let hintRace: Race | null = null;
    const hinted = new Set<Hint>();
    let hintAt = 0;
    const learnt = (kind: Hint) => {
      hints[kind] = 3;
      try {
        localStorage.setItem(HINTS_KEY, JSON.stringify(hints));
      } catch {
        // Shown again next time, then.
      }
    };
    const hint = (kind: Hint, now: number) => {
      const el = hintRef.current;
      if (!el || hints[kind] >= 3 || hinted.has(kind) || now - hintAt < 4000) return;
      hinted.add(kind);
      hintAt = now;
      hints[kind]++;
      try {
        localStorage.setItem(HINTS_KEY, JSON.stringify(hints));
      } catch {
        // Not counted, then.
      }
      el.textContent = HINTS[kind];
      el.animate([{ opacity: 0 }, { opacity: 1, offset: 0.1 }, { opacity: 1, offset: 0.85 }, { opacity: 0 }], { duration: 3500 });
    };
    /** Seconds the player's been facing the wrong way. */
    let wrongFor = 0;
    /** The player's item last frame: gone since is an item used. */
    let lastItem: ItemKind | null = null;
    /** The player's boost last frame: a jump in it is a boost starting, and a whoosh. */
    let lastBoost = 0;
    /** How many puddles of beer, and of sick or sauce, the player had on the road last frame: one more is one spilled. */
    let lastPuddles = [0, 0, 0];
    /** What the player feels of what just happened to them. */
    const feel = (e: RaceEvent, race: Race) => {
      if (e.kind === "hit") {
        rendererRef.current?.kick(TUNING.juice.shakeHit * (e.spin ? 1 : 0.5));
        hapticTick();
        if (e.what === "beer") sfx.spill();
        else if (e.what === "barf" || e.what === "bearnaise") sfx.spill(true);
        else sfx.hit();
        flashRef.current?.animate([{ opacity: 0.8 }, { opacity: 0 }], { duration: 450, easing: "ease-out" });
      } else if (e.kind === "land") rendererRef.current?.kick(TUNING.juice.shakeLand);
      else if (e.kind === "drift") learnt("drift");
      else if (e.kind === "block") {
        hapticTick();
        sfx.charge(1);
        const pop = popsRef.current?.children[popAt++ % (popsRef.current?.children.length || 1)] as HTMLElement | undefined;
        if (pop) {
          pop.textContent = "Skjold!";
          pop.animate([{ opacity: 0, transform: "translate(-50%, 0)" }, { opacity: 1, offset: 0.15 }, { opacity: 0, transform: "translate(-50%, -60px)" }], { duration: 900 });
        }
      }
      else if (e.kind === "box" || e.kind === "charge" || e.kind === "trick") {
        hapticTick();
        if (e.kind === "box") {
          const roll = TUNING.items.rollTime;
          sfx.roulette(roll);
          setReels({ key: e.t, faces: e.reels });
          if (e.frenzy) {
            sfx.jackpot(roll);
            setTimeout(() => setBanner({ key: e.t, text: "Jackpot!" }), roll * 1000);
          }
        } else if (e.kind === "charge") sfx.charge(e.tier);
        else {
          sfx.trick();
          learnt("trick");
        }
      } else if (e.kind === "finish") {
        // The song plays on under the results: full band if you were on the podium, stripped back if not.
        const win = (race.karts.find((k) => k.player)?.place ?? 1) <= 3;
        outro(win);
        sfx.finish(win);
      } else if (e.kind === "lap") {
        if (e.lap >= 1) splits.current.push(e.t);
        if (race.laps > 1 && e.lap === race.laps - 1) {
          setBanner({ key: e.t, text: "Sidste omgang!" });
          sfx.lastLap();
          music(true, 1.08, race.track.map.key);
        }
      } else if (e.kind === "score" && e.what !== "finish" && e.what !== "rival") {
        // A popup rising from above the wheel, the next in turn of a few, a little to one side.
        const pops = popsRef.current;
        const pop = pops?.children[popAt++ % (pops?.children.length || 1)] as HTMLElement | undefined;
        if (pop) {
          pop.textContent = `+${e.points} ${SCORE_LABELS[e.what] ?? ""}`;
          const x = ((popAt % 3) - 1) * 36;
          pop.animate(
            [
              { transform: `translate(calc(-50% + ${x}px), 0) scale(0.7)`, opacity: 0 },
              { transform: `translate(calc(-50% + ${x}px), -10px) scale(1.1)`, opacity: 1, offset: 0.15 },
              { transform: `translate(calc(-50% + ${x}px), -70px) scale(1)`, opacity: 0 },
            ],
            { duration: 1000, easing: "ease-out" },
          );
        }
        comboRef.current?.animate([{ transform: "scale(1.35)" }, { transform: "scale(1)" }], { duration: 250, easing: "ease-out" });
      } else if (e.kind === "start") {
        setBanner({ key: -1, text: e.good ? "Raketstart!" : "For tidligt!" });
        if (e.good) hapticTick();
      }
    };
    let shownFailures = 0;
    const minimap = { track: null as Track | null, image: null as HTMLCanvasElement | null, x0: 0, z0: 0, scale: 1, faces: new Map<string, HTMLCanvasElement>() };
    // Frame pacing, for the resolution to follow and the debug readout.
    let frameMs = 16.7;
    let judgedAt = last;
    let slowChecks = 0;
    let statsAt = last;
    const debug = isDebugOn();
    let worstRecent = 0;
    let worstAt = last;
    /** The last five frames over 50 ms, and what the frame before each was doing (debug readout). */
    const hitches: string[] = [];
    const hitch = { js: 0, events: "", hud: false, resized: false };

    // How long our own code takes a frame, for the debug readout.
    let jsMs = 0;
    let jsWorst = 0;
    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      if (!debug) return run(now);
      const started = performance.now();
      run(now);
      const took = performance.now() - started;
      hitch.js = took;
      jsMs += (took - jsMs) * 0.05;
      jsWorst = Math.max(jsWorst, took);
      if (probe.current.at) probe.current.js = Math.max(probe.current.js, took);
    };
    const run = (now: number) => {
      const interval = now - last;
      const dt = Math.min(interval / 1000, 0.1);
      if (raceRef.current?.phase === "racing" && !raceRef.current.autopilot && interval < 1000) {
        tally.current.frames++;
        tally.current.ms += interval;
      }
      last = now;
      if (interval < 100) frameMs += (interval - frameMs) * 0.1;
      if (debug) {
        if (now - worstAt > 1000) {
          worstAt = now;
          worstRecent = 0;
          jsWorst = 0;
        }
        worstRecent = Math.max(worstRecent, interval);
        // A frame that came late: what the frame before it did, kept for the readout.
        // (The renderer's stats and the shots are still the last frame's: this one hasn't stepped or drawn yet.)
        const r = rendererRef.current;
        if (interval > 50 && raceRef.current?.phase === "racing" && r) {
          hitches.unshift(`${interval.toFixed(0)} ms: js ${hitch.js.toFixed(1)} · ${hitch.events || "no events"} · ${raceRef.current.shots.length} shots · ${r.stats.particles} fx · ${r.stats.draws} draws${hitch.hud ? " · HUD" : ""}${hitch.resized ? " · resized" : ""}`);
          hitches.length = Math.min(hitches.length, 5);
        }
        hitch.events = "";
        hitch.hud = hitch.resized = false;
        const pr = probe.current;
        if (pr.at) {
          if (now - pr.at < 600) pr.worst = Math.max(pr.worst, interval);
          else {
            pr.report = `press: worst ${pr.worst.toFixed(0)} ms · js ${pr.js.toFixed(1)} ms · touch ${pr.lag.toFixed(0)} ms late (moves ${pr.moveLag.toFixed(0)}) · resized ${pr.resizes > pr.resizesAtPress ? "yes" : "no"}`;
            pr.at = 0;
          }
        }
      }
      if (now - judgedAt > 1000) {
        judgedAt = now;
        const v = view.current;
        // Missing a 60 Hz refresh two checks running: a step down.
        slowChecks = frameMs > 21 ? slowChecks + 1 : 0;
        if (slowChecks >= 2 && v.level < levels().length - 1) {
          v.level++;
          slowChecks = 0;
          applySize.current();
          hitch.resized = true;
        }
      }
      if (debug && statsRef.current && now - statsAt > 500) {
        statsAt = now;
        statsRef.current.textContent = `${Math.round(1000 / frameMs)} fps · ${frameMs.toFixed(1)} ms · ${levels()[view.current.level]}x · ${rendererRef.current?.kind ?? "–"} · ${rendererRef.current?.stats.draws ?? 0} draws · ${Math.round((rendererRef.current?.stats.triangles ?? 0) / 1000)}k tris · worst ${worstRecent.toFixed(0)} ms · js ${jsMs.toFixed(1)}/${jsWorst.toFixed(1)} ms\n${where(raceRef.current)}${balance(raceRef.current)}${probe.current.report}${hitches.length ? `\nslow frames, and what the frame before did:\n${hitches.join("\n")}` : ""}`;
      }
      const race = raceRef.current;
      if (!race) return;

      const keys = input.current.keys;
      const keySteer = (keys.has("right") ? 1 : 0) - (keys.has("left") ? 1 : 0);
      const steer = keySteer || input.current.steer;
      // Paused: the clock stops, the picture stays.
      if (!pausedRef.current) carry += dt;
      while (carry >= STEP) {
        step(race, {
          steer,
          fire: input.current.fire,
          trick: input.current.trick,
          back: input.current.back,
          held: input.current.touch || keys.size > 0 || input.current.fire,
          // Steering by keyboard, the drift key drifts; by touch, holding full lock does.
          drift: keySteer ? keys.has("drift") : undefined,
        });
        if (!race.autopilot) follow(tally.current.sectors, race, race.karts.find((k) => k.player)!);
        input.current.fire = input.current.trick = input.current.back = false;
        carry -= STEP;
      }
      const player = race.karts.find((k) => k.player)!;
      for (const e of race.events) {
        if (debug) hitch.events += `${e.kind} `;
        if (race.autopilot) continue;
        count(tally.current, e, player.id);
        note(tally.current.sectors, race, player, e);
        if (e.kart === player.id) feel(e, race);
      }
      race.events.length = 0;
      // Drawn between the last two sim steps, `carry` of the way to the next.
      rendererRef.current?.render(race, carry / STEP, dt);
      if (knobRef.current) knobRef.current.style.transform = `translateX(${steer * 22}px)`;
      const joy = input.current.touch ? "1" : "0.3";
      if (joyRef.current && joyRef.current.style.opacity !== joy) joyRef.current.style.opacity = joy;

      if (!race.autopilot) {
        const lap = Math.min(race.laps, Math.max(1, player.lap + 1));
        const count = Math.ceil(COUNTDOWN - race.time);
        const rolling = player.roll > 0;
        const rival = race.karts.find((k) => k.id === race.rival);
        const rivalAhead = rival ? rival.place < player.place : null;
        if (timerRef.current) timerRef.current.textContent = formatTime(player.finishTime ?? raceTime(race));
        // The first-time hints, as the moment for each comes up.
        if (hintRace !== race) {
          hintRace = race;
          hinted.clear();
          lastPuddles = [0, 0, 0];
        }
        if (race.phase === "racing" && player.finishTime === null) {
          if (player.speed > MAX_SPEED * 0.6 && Math.abs(steer) > 0.5 && !player.drift) hint("drift", now);
          if (player.trick > 0 && !player.tricked) hint("trick", now);
          if (player.item && player.roll <= 0) hint("item", now);
          if (lastItem && !player.item) learnt("item");
        }
        lastItem = player.item;
        // Facing back the way you came, for a second: say so.
        const along = race.track.samples[player.idx];
        const backwards = race.phase === "racing" && player.finishTime === null && player.spin <= 0 &&
          Math.cos(player.heading) * along.tx + Math.sin(player.heading) * along.tz < -0.3;
        wrongFor = backwards ? wrongFor + dt : 0;
        if (wrongRef.current) wrongRef.current.style.opacity = wrongFor > 1 ? "1" : "0";
        // Your line, for a ghost of this run.
        if (race.phase === "racing" && player.finishTime === null) {
          const round = (v: number) => Math.round(v * 100) / 100;
          const ground = roadY(race.track, player.idx, player.x, player.z) + player.y;
          for (const t = raceTime(race); lineAt.current <= t; lineAt.current += GHOST_STEP) {
            line.current.push(round(player.x), round(ground), round(player.z), round(player.heading));
          }
        }
        const sc = race.score;
        const total = sc.total.toLocaleString("da-DK");
        if (scoreRef.current && scoreRef.current.textContent !== total) scoreRef.current.textContent = total;
        if (comboRef.current && comboBarRef.current) {
          // The combo: what the next thing done is multiplied by, and the time left to do it in.
          comboRef.current.style.opacity = sc.chain ? "1" : "0";
          const next = `×${Math.min(1 + TUNING.combo.step * sc.chain, TUNING.combo.maxMult).toFixed(1).replace(".", ",")}`;
          const label = comboRef.current.firstChild as Text | null;
          if (label && label.data !== next) label.data = next;
          comboBarRef.current.style.transform = `scaleX(${sc.left / TUNING.combo.window})`;
        }
        if (linesRef.current) {
          // Speed lines while boosting, the harder the boost the stronger, flickering.
          const lines = player.boost > 0 ? Math.min(1, (player.boostMul - 1) / 0.55) : 0;
          linesRef.current.style.opacity = String(lines * 0.45);
          if (vignetteRef.current) vignetteRef.current.style.opacity = String(lines);
          if (lines) linesRef.current.style.transform = `rotate(${Math.random() * 7}deg)`;
        }
        const warn = warnRef.current;
        if (warn) {
          const threat = pausedRef.current ? null : threatTo(race, player);
          warn.style.opacity = threat ? "1" : "0";
          if (threat) {
            // Only what's coming is drawn: the glove, the can, the swarm or the rocket.
            THREATS.forEach((kind, i) => ((warn.children[i] as HTMLElement).style.display = kind === threat.shot.kind ? "flex" : "none"));
            // Along the bottom edge on its side, pulsing faster as it closes in.
            warn.style.left = `${50 + threat.side * 38}%`;
            warn.style.transform = `translateX(-50%) scale(${1 + 0.15 * Math.sin(now / (160 - threat.near * 110))})`;
          }
        }
        // Sound: the count, the engine, a whoosh as a boost kicks in.
        if (count !== shown.count && count >= 0 && count <= COUNTDOWN) {
          sfx.count(count === 0);
        }
        engineAt(Math.min(1.5, player.speed / MAX_SPEED), race.phase === "racing" && !pausedRef.current && player.finishTime === null, {
          drift: player.drift !== 0,
          boost: player.boost > 0,
        });
        // The weapons: beer spilled by you, and what's within earshot — the smoke machine, the Soundboks's techno, the mosquitoes.
        let beer = 0;
        let mess = 0;
        let sick = 0;
        for (const sh of race.shots) {
          if (sh.owner !== player.id || !isPuddle(sh)) continue;
          if (sh.kind === "beer") beer++;
          else if (sh.kind === "barf") sick++;
          else mess++;
        }
        // Sick lands the moment the head turns, and has its own sound.
        if (sick > lastPuddles[2]) sfx.barf();
        else if (beer > lastPuddles[0]) sfx.spill();
        else if (mess > lastPuddles[1]) sfx.spill(true);
        lastPuddles = [beer, mess, sick];
        ambience(race.phase === "racing" && !pausedRef.current ? earshot(race, player) : QUIET);
        if (player.boost > lastBoost + 0.1) sfx.boost(Math.min(1, (player.boostMul - 1) / 0.55));
        lastBoost = player.boost;
        // The music quickens for a frenzy, and settles back after.
        const frenzy = player.frenzy > 0 && player.roll <= 0;
        if (frenzy !== shown.frenzy && player.finishTime === null) music(true, frenzy ? 1.25 : lap === race.laps && race.laps > 1 ? 1.08 : 1, race.track.map.key);
        if (
          player.place !== shown.place ||
          lap !== shown.lap ||
          race.laps !== shown.laps ||
          rivalAhead !== shown.rivalAhead ||
          player.item !== shown.item ||
          !sameItems(player.queue, shown.queue) ||
          rolling !== shown.rolling ||
          frenzy !== shown.frenzy ||
          count !== shown.count
        ) {
          Object.assign(shown, { place: player.place, lap, laps: race.laps, rivalAhead, item: player.item, queue: [...player.queue], rolling, frenzy, count });
          setHud({ ...shown });
          hitch.hud = true;
        }
        if (race.phase === "done") {
          doneAt ||= now;
          // A moment to see yourself cross the line before the table covers it.
          if (now - doneAt > 1800) {
            doneAt = 0;
            // Faster than the ghost: this run's the ghost now — on the phone, and
            // for the lodge if it's their fastest (or there's none saved yet).
            // Only a race against the field, without help, counts: for the ghost, the records, your best.
            const counts = countsFor(race);
            const time = player.finishTime;
            const id = trackId(race.track.map);
            if (counts && time !== null && time < (loadGhostHere(id)?.time ?? Infinity)) {
              saveGhostHere(id, { time, name: meRef.current.name, emoji: meRef.current.emoji, path: line.current });
            }
            const lodgeRecord = counts && time !== null && time < (lodgeTime.current ?? Infinity);
            if (counts && !tally.current.saved) {
              tally.current.saved = true;
              onRunRef.current?.(runOf(race, tally.current, splits.current, false, rendererRef.current?.kind ?? "", lodgeRecord ? line.current : undefined));
            }
            ambience(QUIET);
            race.autopilot = true;
            setResults([...race.karts].sort((a, b) => a.place - b.place));
            const summary = summarise(race, splits.current);
            // (After `race.autopilot = true` above, so the race itself no longer says.)
            summary.counts = counts;
            // Your best for this track and difficulty, kept on the phone.
            const key = `${trackId(race.track.map)}:${race.difficulty}`;
            const kept = loadBest();
            summary.before = kept[key] ?? null;
            // The score under `key`, the fastest time under `key:t`: the map cards show them even before the lodge's records load.
            if (counts) {
              const next = { ...kept };
              if (summary.score > (kept[key] ?? -1)) next[key] = summary.score;
              if (time !== null && time < (kept[`${key}:t`] ?? Infinity)) next[`${key}:t`] = time;
              saveBest(next);
            }
            setBest(loadBest());
            setFinish(summary);
            setScreen("results");
          }
        }
      }
      drawMinimap(miniRef.current, race, minimap);
      if (modelFailures.size !== shownFailures) {
        shownFailures = modelFailures.size;
        setFailures(shownFailures);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      silence();
    };
  }, []);

  // Keyboard, for desktop: arrows / A-D to steer, shift, down or S to drift,
  // up or W for a trick, space to fire, X to fire the other way, escape to pause.
  useEffect(() => {
    const map: Record<string, string> = {
      ArrowLeft: "left", a: "left", ArrowRight: "right", d: "right", Shift: "drift", ArrowDown: "drift", s: "drift",
    };
    const down = (e: KeyboardEvent) => {
      if (e.key === "Escape" && screenRef.current === "race") pause(!pausedRef.current);
      else if (map[e.key]) input.current.keys.add(map[e.key]);
      else if (e.key === " ") input.current.fire = true;
      else if (e.key === "ArrowUp" || e.key === "w") input.current.trick = true;
      else if (e.key === "x") input.current.back = true;
      else return;
      e.preventDefault();
    };
    const up = (e: KeyboardEvent) => map[e.key] && input.current.keys.delete(map[e.key]);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [pause]);

  // Away from the app mid-race — a call, another app — and the race waits.
  useEffect(() => {
    const hidden = () => {
      if (document.hidden && screenRef.current === "race" && raceRef.current?.phase !== "done") pause(true);
    };
    document.addEventListener("visibilitychange", hidden);
    return () => document.removeEventListener("visibilitychange", hidden);
  }, [pause]);

  // Holding a finger down to steer is, to iOS, a long press: it answers by
  // selecting the nearest text — the copy/paste callout and the loupe — and
  // that text can be anywhere on the page: the tab bar, the game switcher. So
  // while the game is up, selection and the long-press menu are off for the
  // whole document. Touches themselves are left alone: cancelling them needs a
  // non-passive touchstart listener, and iOS holds its own frame updates at
  // every press until such a listener has answered — a dropped frame, a jolt,
  // each time a finger goes down to steer.
  useEffect(() => {
    const root = document.documentElement.style as CSSStyleDeclaration & { webkitTouchCallout: string };
    const saved = [root.userSelect, root.webkitUserSelect, root.webkitTouchCallout];
    root.userSelect = root.webkitUserSelect = root.webkitTouchCallout = "none";
    const cancel = (e: Event) => e.preventDefault();
    document.addEventListener("selectstart", cancel);
    document.addEventListener("contextmenu", cancel);
    return () => {
      [root.userSelect, root.webkitUserSelect, root.webkitTouchCallout] = saved;
      document.removeEventListener("selectstart", cancel);
      document.removeEventListener("contextmenu", cancel);
    };
  }, []);

  // Touch: drag sideways to steer, from wherever the finger went down; a
  // quick tap that barely moves fires the item — and so does a second finger
  // put down while the first steers, so firing never means letting go.
  //
  // Not through React, whose listeners sit at the root of the app on every
  // page: while racing, a plain element outside React's part of the page lies
  // over the game, below the HUD strip with the item and ✕, and all a touch
  // reaches is the few lines here.
  const placeSurface = useRef(() => {});
  useEffect(() => {
    const surface = document.createElement("div");
    const css = surface.style;
    css.position = "fixed";
    // Over the full-screen game (z-40), under anything the app puts above it.
    css.zIndex = "41";
    css.touchAction = "none";
    css.userSelect = css.webkitUserSelect = "none";
    css.setProperty("-webkit-touch-callout", "none");
    document.body.appendChild(surface);
    const place = () => {
      const r = containerRef.current?.getBoundingClientRect();
      if (!r || screenRef.current !== "race" || pausedRef.current) {
        css.display = "none";
        return;
      }
      const top = r.top + HUD_STRIP;
      css.display = "block";
      css.left = `${r.left}px`;
      css.top = `${top}px`;
      css.width = `${r.width}px`;
      css.height = `${Math.max(0, r.bottom - top)}px`;
    };
    placeSurface.current = place;

    /** The finger steering: where it went down, when, how far it's wandered — and where a flick up would be measured from. */
    let touch: { id: number; x: number; y: number; t: number; moved: number; fy: number; ft: number; fired: boolean } | null = null;
    const down = (e: PointerEvent) => {
      if (touch) {
        if (e.pointerId !== touch.id) input.current.fire = true;
        return;
      }
      if (isDebugOn()) {
        const now = performance.now();
        Object.assign(probe.current, { at: now, worst: 0, js: 0, lag: now - e.timeStamp, resizesAtPress: probe.current.resizes });
      }
      // A mouse is captured so a drag can leave the game; a finger's touches
      // stay with the element they started on anyway.
      if (e.pointerType !== "touch") surface.setPointerCapture(e.pointerId);
      touch = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), moved: 0, fy: e.clientY, ft: performance.now(), fired: false };
      input.current.touch = true;
    };
    const move = (e: PointerEvent) => {
      if (!touch || touch.id !== e.pointerId) return;
      if (isDebugOn()) probe.current.moveLag += (performance.now() - e.timeStamp - probe.current.moveLag) * 0.1;
      const dx = e.clientX - touch.x;
      touch.moved = Math.max(touch.moved, Math.hypot(dx, e.clientY - touch.y));
      const past = Math.max(0, Math.abs(dx) - STEER_DEAD);
      input.current.steer = Math.sign(dx) * Math.min(1, past / (steerDrag.current - STEER_DEAD));
      // A flick up (a trick) or down (the item the other way): far enough,
      // quickly enough — measured from where the finger was a moment ago.
      const now = performance.now();
      if (now - touch.ft > 180) Object.assign(touch, { fy: e.clientY, ft: now });
      const flick = touch.fy - e.clientY;
      if (Math.abs(flick) > TUNING.trick.swipePx) {
        if (flick > 0) input.current.trick = true;
        // One item a swipe: a long flick down would otherwise fire the whole queue.
        else if (!touch.fired) input.current.back = touch.fired = true;
        Object.assign(touch, { fy: e.clientY, ft: now });
      }
    };
    const up = (e: PointerEvent) => {
      if (!touch || touch.id !== e.pointerId) return;
      if (touch.moved < 12 && performance.now() - touch.t < 300) input.current.fire = true;
      touch = null;
      input.current.touch = false;
      input.current.steer = 0;
    };
    const passive = { passive: true };
    surface.addEventListener("pointerdown", down, passive);
    surface.addEventListener("pointermove", move, passive);
    surface.addEventListener("pointerup", up, passive);
    surface.addEventListener("pointercancel", up, passive);
    // Cancelled as they start, or two quick taps in a row bring up iOS's
    // magnifier.
    surface.addEventListener("touchstart", (e) => e.preventDefault(), { passive: false });
    const observer = new ResizeObserver(place);
    observer.observe(containerRef.current!);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, passive);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place);
      surface.remove();
    };
  }, []);
  useEffect(() => placeSurface.current(), [screen, paused]);
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 1500);
    return () => clearTimeout(t);
  }, [banner]);

  const racing = screen === "race";
  const rivalId = raceRef.current?.rival;
  const onChallenge = mapKey === challenge.mapKey && variant === challenge.variant && difficulty === challenge.difficulty;
  const board = records
    .filter((b) => b.track === trackId(map) && b.difficulty === difficulty)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);
  const rivalRacer = racers.find((r) => r.id === rivalId);
  const countdown = racing && hud.count > 0 ? String(hud.count) : null;
  const training = mode === "training";

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden bg-ink select-none"
      style={{ touchAction: "none", WebkitTouchCallout: "none", WebkitUserSelect: "none" }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 block w-full h-full" />
      {racing && !CALM && (
        // Speed lines on a boost: thin white streaks, faint, at the edges of the screen only.
        <div
          ref={linesRef}
          aria-hidden
          className="absolute -inset-[10%] pointer-events-none opacity-0"
          style={{
            background: "repeating-conic-gradient(from 0deg at 50% 45%, rgba(255,255,255,0.7) 0deg 0.35deg, transparent 0.35deg 7deg)",
            // Clear over the road and the kart; the streaks only reach in from the very edge.
            maskImage: "radial-gradient(ellipse 60% 55% at 50% 45%, transparent 70%, black 100%)",
            WebkitMaskImage: "radial-gradient(ellipse 60% 55% at 50% 45%, transparent 70%, black 100%)",
            // Its own layer, so the turn each frame moves pixels rather than painting them.
            willChange: "transform, opacity",
          }}
        />
      )}
      {racing && !CALM && (
        // A boost darkens the corners a little, as the lines streak past.
        <div
          ref={vignetteRef}
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-0"
          style={{ background: "radial-gradient(ellipse 75% 65% at 50% 50%, transparent 60%, rgba(10,8,30,0.45) 100%)", willChange: "opacity" }}
        />
      )}
      {racing && hud.frenzy && (
        // A frenzy: the edge of the screen glowing, pulsing.
        <m.div
          aria-hidden
          animate={{ opacity: [1, 0.55, 1] }}
          transition={{ duration: 0.5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 pointer-events-none"
          // Gradients, not blurred inset shadows: Safari paints those slowly, and again whenever they reappear.
          style={{
            background: "radial-gradient(ellipse 80% 70% at 50% 50%, transparent 55%, rgba(30,170,255,0.55) 85%, rgba(255,40,190,0.9) 100%)",
            willChange: "opacity",
          }}
        />
      )}
      {/* A red flash round the edge of the screen when you're hit. */}
      <div
        ref={flashRef}
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-0"
        style={{ background: "radial-gradient(ellipse 80% 70% at 50% 50%, transparent 55%, rgba(230,30,20,0.85) 100%)", willChange: "opacity" }}
      />

      {failed && (
        <div className="absolute inset-0 flex items-center justify-center px-8 text-center font-mono text-[11px] text-paper/70">
          Banen kunne ikke tegnes på denne enhed.
        </div>
      )}

      {/* HUD */}
      <canvas
        ref={miniRef}
        width={180}
        height={180}
        className={`absolute left-2 top-16 w-[88px] h-[88px] pointer-events-none transition-opacity ${racing ? "opacity-90" : "opacity-0"}`}
      />
      {racing && (
        <>
          <div className="absolute left-3 top-1 pointer-events-none leading-none">
            {/* Bounces each time your place changes. */}
            <m.span
              key={hud.place}
              initial={{ scale: 1.45 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 520, damping: 14 }}
              className="inline-block origin-bottom-left text-[68px]"
            >
              <Chunky>{hud.place}</Chunky>
            </m.span>
            <span className="text-[28px]">
              <Chunky>.</Chunky>
            </span>
          </div>
          {ghostShown && (
            // The ghost you're racing: whose, and their time.
            <div className="absolute left-2 top-[186px] flex items-center gap-1 rounded-full bg-ink/60 border border-[#9fd8ff] pl-1 pr-2 py-0.5 pointer-events-none font-mono text-[8px] tracking-[1px] text-white">
              <GhostIcon size={14} /> {ghostShown.name} {formatTime(ghostShown.time)}
            </div>
          )}
          {hud.rivalAhead !== null && (
            // The rival: who, and whether they're ahead of you or behind.
            <div className="absolute left-2 top-[158px] flex items-center gap-1 rounded-full bg-ink/60 border border-[#e3342f] pl-0.5 pr-2 py-0.5 pointer-events-none">
              <span className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center text-[14px] leading-none">
                {rivalRacer && <Face racer={rivalRacer} />}
              </span>
              <span className="font-mono text-[8px] tracking-[1.2px] uppercase text-white">
                Rival {hud.rivalAhead ? "▲" : "▼"}
              </span>
            </div>
          )}
          {/* The item slot: a white frame round dark glass, a glint crossing it now and then. */}
          <div className="absolute left-1/2 -translate-x-1/2 top-3 w-[78px] h-[78px] rounded-full border-[4px] border-white bg-ink/45 flex items-center justify-center pointer-events-none shadow-[0_3px_0_rgba(0,0,0,0.35),inset_0_0_0_2px_rgba(26,18,6,0.55)]">
            <span aria-hidden className="absolute inset-0 rounded-full overflow-hidden">
              <span className="absolute -inset-y-2 left-1/4 w-3 bg-white/25 animate-shine motion-reduce:hidden" />
            </span>
            {hud.rolling && <span className="absolute -inset-1 rounded-full border-4 border-[#ffd23a] animate-ping motion-reduce:animate-none" />}
            <Roulette item={hud.item} rolling={hud.rolling} />
          </div>
          {reels && (
            <div className="absolute left-1/2 -translate-x-1/2 top-[88px] pointer-events-none">
              <Reels key={reels.key} faces={reels.faces} onDone={() => setReels(null)} />
            </div>
          )}
          {hud.queue.length > 0 && !hud.rolling && !reels && (
            // Zobbe's next three, in the order they'll come up.
            <div className="absolute left-1/2 -translate-x-1/2 top-[88px] flex gap-1.5 pointer-events-none">
              {hud.queue.map((kind, i) => (
                <m.span
                  key={`${kind}-${hud.queue.slice(0, i).filter((k) => k === kind).length}`}
                  initial={{ scale: 0.3, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 22, delay: i * 0.08 }}
                  className="w-[38px] h-[38px] rounded-full border-2 border-white/80 bg-ink/60 flex items-center justify-center p-[3px] text-[20px] leading-none"
                >
                  <ItemIcon kind={kind} />
                </m.span>
              ))}
            </div>
          )}
          <button
            type="button"
            aria-label="Afslut løbet"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => pause(true)}
            className="absolute right-3 top-3 w-11 h-11 rounded-full border-2 border-white/90 bg-ink/60 text-white font-mono text-sm flex items-center justify-center"
          >
            ✕
          </button>
          {/* Your score, and the combo running: what the next thing done is worth, and the time left to do it. */}
          <div
            ref={scoreRef}
            style={HUD_OUTLINE}
            className="absolute right-3 top-[60px] pointer-events-none font-display font-extrabold italic text-white text-[22px] leading-none tabular-nums"
          />
          <div
            ref={comboRef}
            className="absolute right-3 top-[88px] pointer-events-none opacity-0 transition-opacity duration-150 rounded-full bg-[#ffd23a] text-ink font-display font-extrabold italic text-[14px] leading-none px-2 pt-1 pb-1.5 overflow-hidden border-2 border-ink"
          >
            ×1,0
            <div ref={comboBarRef} className="absolute left-0 right-0 bottom-0 h-[3px] bg-ink/60 origin-left" />
          </div>
          <div
            ref={wrongRef}
            style={HUD_OUTLINE}
            className="absolute inset-x-0 top-[24%] text-center pointer-events-none opacity-0 transition-opacity duration-200 font-display font-extrabold italic uppercase text-[30px] text-[#ff4a3d]"
          >
            ↺ Forkert vej!
          </div>
          <div className="absolute inset-x-6 bottom-[190px] flex justify-center pointer-events-none">
            <div
              ref={hintRef}
              className="opacity-0 text-center font-mono text-[10px] leading-snug tracking-wide text-paper bg-ink/75 px-3 py-1.5 rounded-full"
            />
          </div>
          <div ref={popsRef} aria-hidden className="absolute inset-0 pointer-events-none">
            {[0, 1, 2, 3, 4].map((i) => (
              <span
                key={i}
                style={HUD_OUTLINE}
                className="absolute left-1/2 bottom-[150px] opacity-0 whitespace-nowrap font-display font-extrabold italic uppercase text-[19px] text-[#ffd23a]"
              />
            ))}
          </div>
          {isDebugOn() && (
            <div
              ref={statsRef}
              className="absolute right-3 top-[124px] pointer-events-none font-mono text-[9px] text-white bg-ink/60 rounded px-1.5 py-0.5 whitespace-pre text-right"
            />
          )}
          <div style={HUD_OUTLINE} className="absolute left-3 bottom-3 pointer-events-none flex items-center gap-1.5 font-display font-extrabold italic text-white text-[26px] leading-none">
            <FlagIcon size={22} /> {hud.lap}/{hud.laps}
          </div>
          {training && (
            <div className="absolute left-1/2 -translate-x-1/2 bottom-[86px] pointer-events-none font-mono text-[9px] tracking-[1.4px] uppercase text-[#9cc4ff] bg-ink/70 border border-[#2f7de1] px-2.5 py-1 rounded-full">
              Træning · tæller ikke
            </div>
          )}
          <div
            ref={timerRef}
            style={HUD_OUTLINE}
            className="absolute right-3 bottom-3 pointer-events-none font-display font-extrabold italic text-white text-[22px] leading-none tabular-nums"
          />
          {/* Something coming for you from behind: which side, and what. */}
          <div
            ref={warnRef}
            aria-hidden
            className="absolute bottom-[92px] w-11 h-11 rounded-full border-[3px] border-[#e3342f] bg-ink/70 flex items-center justify-center text-[22px] leading-none pointer-events-none opacity-0 transition-opacity duration-150"
          >
            {THREATS.map((kind) => (
              <span key={kind} style={{ display: "none" }} className="w-7 h-7 items-center justify-center">
                <ItemIcon kind={kind === "can" ? "tuborg" : kind} />
              </span>
            ))}
          </div>
          {/* The stick: faint until a finger's on the glass. */}
          <div
            ref={joyRef}
            className="absolute left-1/2 -translate-x-1/2 bottom-3 w-[72px] h-[72px] rounded-full border-2 border-white/70 bg-white/10 flex items-center justify-center pointer-events-none opacity-30 transition-opacity duration-150"
          >
            <div ref={knobRef} className="w-5 h-5 rounded-full bg-white/90" />
          </div>
          <AnimatePresence>
            {countdown && (
              <m.div
                key={countdown}
                initial={{ scale: 1.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.6, opacity: 0 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="absolute inset-x-0 top-[22%] flex justify-center pointer-events-none text-[110px] leading-none"
              >
                <Chunky>{countdown}</Chunky>
              </m.div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {banner && (
              <m.div
                key={banner.key}
                initial={{ scale: 1.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25, ease: EASE }}
                className="absolute inset-x-0 top-[24%] text-center pointer-events-none uppercase text-[38px] leading-none"
              >
                <Chunky>{banner.text}</Chunky>
              </m.div>
            )}
          </AnimatePresence>
          {hud.count > 0 && pillsShown && (
            // How to drive: once a session, low on the screen, gone after three seconds.
            <div className="absolute inset-x-0 bottom-[92px] flex flex-col items-center gap-1 pointer-events-none animate-brief">
              <span className="font-mono text-[8px] tracking-[1.2px] uppercase text-paper/90 bg-ink/60 px-2 py-0.5 rounded-full">
                Træk til siden for at styre · tryk for at skyde
              </span>
              <span className="font-mono text-[8px] tracking-[1.2px] uppercase text-paper/90 bg-ink/60 px-2 py-0.5 rounded-full">
                Sæt fingeren ned på 1 for raketstart
              </span>
            </div>
          )}
          {paused && (
            <div className="absolute inset-0 flex items-end bg-ink/40">
              <div className="w-full m-2 rounded-lg bg-ink/90 backdrop-blur-md text-paper p-3.5 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="font-display text-[19px] font-extrabold uppercase tracking-tight">Pause</div>
                  <MuteButton mute={mute} onToggle={toggleMute} />
                </div>
                <button
                  type="button"
                  onClick={() => pause(false)}
                  className="w-full border-0 py-3.5 rounded-lg bg-[#ffd23a] text-ink font-display text-sm font-extrabold tracking-wide uppercase"
                >
                  Fortsæt
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      abandon();
                      pause(false);
                      unlock();
                      startRace(false, mode === "training");
                    }}
                    className="flex-1 border border-paper/25 py-3 rounded-lg font-display text-sm font-bold tracking-wide uppercase"
                  >
                    Start forfra
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      abandon();
                      pause(false);
                      setPage("home");
                      startRace(true);
                    }}
                    className="flex-1 border border-paper/25 py-3 rounded-lg font-display text-sm font-bold tracking-wide uppercase"
                  >
                    Afslut løbet
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {(screen === "results" || (screen === "menu" && page === "home")) && onExit && (
        <button
          type="button"
          aria-label="Luk spillet"
          onClick={onExit}
          className="absolute z-20 right-3 top-3 w-11 h-11 rounded-full border-2 border-white/90 bg-ink/60 text-white font-mono text-sm flex items-center justify-center"
        >
          ✕
        </button>
      )}

      <AnimatePresence>
        {screen === "menu" && !failed && (
          <KartMenu
            key="menu"
            me={me}
            page={page}
            setPage={setPage}
            mode={mode}
            setMode={setMode}
            mapKey={mapKey}
            setMapKey={setMapKey}
            variant={variant}
            setVariant={setVariant}
            difficulty={difficulty}
            setDifficulty={setDifficulty}
            records={records}
            best={best}
            assists={assists}
            toggleAssist={toggleAssist}
            sensitivity={sensitivity}
            setSensitivity={(k) => {
              setSensitivity(k);
              try {
                localStorage.setItem(SENSITIVITY_KEY, k);
              } catch {
                // Not kept, then.
              }
            }}
            challenge={challenge}
            week={week}
            mute={mute}
            toggleMute={toggleMute}
            onStart={() => {
              unlock();
              startRace(false, mode === "training");
            }}
            onExit={onExit}
            notice={
              failures > 0
                ? `${failures} 3D-model${failures > 1 ? "ler" : ""} kunne ikke hentes — viser simpel grafik. ${Array.from(modelFailures, ([name, why]) => `${name}: ${why}`).join(" · ")}`
                : undefined
            }
          />
        )}

        {screen === "results" && (
          <m.div
            key="results"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="absolute inset-x-2 bottom-2 max-h-[calc(100%-16px)] overflow-y-auto rounded-lg bg-ink/90 backdrop-blur-md text-paper p-3.5 flex flex-col gap-2"
          >
            <div className="font-mono text-[8px] tracking-[1.5px] uppercase text-paper/60">
              {map.name}
              {variant !== "normal" && ` ${VARIANTS[variant].toLowerCase()}`} · {finish.training ? "Træning" : DIFFICULTIES[difficulty]}
            </div>
            {!finish.counts && (
              // Not for the records: say why.
              <div className="rounded-lg border border-[#fbbf24]/60 bg-[#fbbf24]/10 px-2.5 py-1.5 font-mono text-[9.5px] leading-snug text-[#fcd34d]">
                {finish.training
                  ? "Træning: din tid og score tæller ikke."
                  : "Du kørte med hjælp: din tid og score tæller ikke på ranglisten."}
              </div>
            )}
            <div className="flex items-end justify-between gap-3">
              <div>
                <div className="font-display text-[30px] leading-none font-extrabold italic tabular-nums">
                  <CountUp to={finish.score} />
                </div>
                <div className="font-mono text-[9px] tracking-wide text-paper/60 mt-1">
                  {!finish.counts
                    ? "Tæller ikke"
                    : finish.before === null
                      ? "Første løb her"
                      : finish.score > finish.before
                        ? `Ny rekord! (før ${finish.before.toLocaleString("da-DK")})`
                        : `Rekord ${finish.before.toLocaleString("da-DK")}`}
                </div>
              </div>
              <Stars count={finish.stars} size={24} animate />
            </div>
            <div className="grid grid-cols-2 gap-x-4 font-mono text-[9.5px] text-paper/80">
              {Object.keys(SCORE_LABELS)
                .filter((what) => finish.by[what])
                .map((what) => (
                  <div key={what} className="flex justify-between">
                    <span>{SCORE_LABELS[what]}</span>
                    <span className="tabular-nums">{finish.by[what].toLocaleString("da-DK")}</span>
                  </div>
                ))}
              <div className="flex justify-between">
                <span>Længste kombo</span>
                <span className="tabular-nums">{finish.bestChain}</span>
              </div>
            </div>
            {onChallenge && !training && week.length > 0 && (
              // This week's challenge, and where everyone stands.
              <div className="font-mono text-[9px] tracking-wide text-paper/70 flex flex-wrap gap-x-3">
                <span className="uppercase text-[#ffd23a] flex items-center gap-1">
                  <TrophyIcon size={13} /> Ugens udfordring
                </span>
                {week.slice(0, 3).map((b, i) => (
                  <span key={b.memberId} className={b.memberId === me.id ? "text-[#ffd23a]" : ""}>
                    {i + 1}. {b.name} {challenge.goal === "score" ? b.score.toLocaleString("da-DK") : formatTime(b.time ?? 0)}
                  </span>
                ))}
              </div>
            )}
            {board.length > 0 && (
              // The lodge's best on this track and difficulty.
              <div className="font-mono text-[9px] tracking-wide text-paper/70 flex flex-wrap gap-x-3">
                <span className="uppercase text-paper/50">Logens bedste</span>
                {board.map((b, i) => (
                  <span key={b.memberId} className={b.memberId === me.id ? "text-[#ffd23a]" : ""}>
                    {i + 1}. {b.name} {b.score.toLocaleString("da-DK")}
                  </span>
                ))}
              </div>
            )}
            {finish.laps.length > 0 && (
              <div className="font-mono text-[9px] tracking-wide text-paper/70">
                Omgange:{" "}
                {finish.laps.map((t, i) => (
                  <span key={i} className={t === Math.min(...finish.laps) && finish.laps.length > 1 ? "text-[#ffd23a]" : ""}>
                    {i > 0 && " · "}
                    {formatTime(t)}
                  </span>
                ))}
              </div>
            )}
            <ol className="flex flex-col gap-1">
              {results.map((k) => (
                <li
                  key={k.id}
                  className={`flex items-center gap-2.5 rounded px-2 py-1 ${k.player ? "bg-[#ffd23a] text-ink" : ""}`}
                >
                  <span className="w-5 font-display font-extrabold italic">{k.place}.</span>
                  <span className="w-7 h-7 rounded-full overflow-hidden flex items-center justify-center text-[18px] leading-none">
                    <Face racer={k} />
                  </span>
                  <span className="font-display font-bold uppercase text-[13px] truncate">{k.name}</span>
                  {k.id === finish.rival && (
                    <span className="font-mono text-[8px] tracking-[1.2px] uppercase rounded-full border border-[#e3342f] px-1.5">Rival</span>
                  )}
                  <span className="ml-auto font-mono text-[10px] opacity-70">
                    {/* Still driving: where they'd finish at the pace of the race. */}
                    {finish.times.get(k.id)?.guess ? "≈ " : ""}
                    {formatTime(finish.times.get(k.id)?.time ?? k.finishTime ?? 0)}
                  </span>
                </li>
              ))}
            </ol>
            <div className="flex gap-2 mt-1">
              <button
                type="button"
                onClick={() => {
                  setPage("home");
                  startRace(true);
                }}
                className="flex-1 border border-paper/25 py-3 rounded-lg font-display text-sm font-bold tracking-wide uppercase"
              >
                Menu
              </button>
              <button
                type="button"
                aria-label="Kør igen"
                title="Kør igen"
                onClick={() => {
                  unlock();
                  startRace(false, mode === "training");
                }}
                className="w-11 shrink-0 border border-paper/25 rounded-lg flex items-center justify-center"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 12a8 8 0 1 1-2.6-5.9" />
                  <path d="M20 4v5h-5" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => {
                  // The next map, in the same variant: pick it, and the race starts once the track has rebuilt.
                  nextRace.current = true;
                  setMapKey(nextMap.key);
                }}
                className="flex-[2] min-w-0 border-0 py-3 px-2 rounded-lg bg-[#ffd23a] text-ink font-display text-sm font-extrabold tracking-wide uppercase truncate"
              >
                Næste: {nextMap.name}
              </button>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** The items in the slot's fruit machine: five, as `animate-roulette` (tailwind.config.js) expects. */
const ROULETTE = Object.keys(ITEMS) as ItemKind[];

/**
 * The item slot, as a fruit machine: a box's item rolls past while it's
 * decided, then the strip stops on it, with a little overshoot, and shows it
 * until it's used. For anyone who's asked for less motion, nothing rolls —
 * just the steady ring round the slot — and the item simply appears.
 *
 * In the slot the whole race and never swapped out for another picture:
 * anything mounted fresh had the phone fetch and decode icons while the game
 * kept it busy, and showed blank until it had. Clipped by clip-path rather
 * than a rounded overflow, which Safari doesn't always apply to a moving layer.
 */
function Roulette({ item, rolling }: { item: ItemKind | null; rolling: boolean }) {
  // Kept after it's used, to fade out on rather than flick back to the first.
  const shown = useRef(item);
  if (item) shown.current = item;
  const land = `-${Math.max(0, ROULETTE.indexOf(shown.current!)) * 70}px`;
  return (
    <span
      className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70px] h-[70px] [clip-path:circle(50%)] transition-[opacity,transform] duration-200 ${item ? "" : "opacity-0 scale-50"}`}
    >
      <span
        style={{ "--land": land, transform: rolling ? undefined : `translateY(${land})` } as CSSProperties}
        className={`flex flex-col ${rolling ? "animate-roulette motion-reduce:invisible" : "animate-roulette-land motion-reduce:animate-none"}`}
      >
        {[...ROULETTE, ROULETTE[0]].map((kind, i) => (
          <span key={i} className="w-[70px] h-[70px] flex-shrink-0 flex items-center justify-center text-[40px] leading-none">
            <ItemIcon kind={kind} />
          </span>
        ))}
      </span>
    </span>
  );
}

/**
 * The results beyond the places: everyone's time — for those still driving
 * when you finished, a guess at where they'd finish going at the race's pace,
 * which keeps them in the order they're in — and your time for each lap.
 */
function summarise(race: Race, splits: number[]): Finish {
  const { n } = race.track;
  const player = race.karts.find((k) => k.player)!;
  const pace = (race.laps * n) / Math.max(1, player.finishTime ?? raceTime(race));
  const times = new Map(
    race.karts.map((k) => [
      k.id,
      k.finishTime !== null
        ? { time: k.finishTime, guess: false }
        : { time: raceTime(race) + Math.max(0, race.laps * n - (k.lap * n + k.idx)) / pace, guess: true },
    ]),
  );
  return {
    times,
    laps: splits.map((t, i) => t - (i ? splits[i - 1] : 0)),
    rival: race.rival,
    score: race.score.total,
    by: race.score.by,
    bestChain: race.score.best,
    stars: starsFor(race.score.total, race.track.map.stars),
    before: null,
    counts: countsFor(race),
    training: race.training,
  };
}

function MuteButton({ mute, onToggle }: { mute: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-label={mute ? "Slå lyden til" : "Slå lyden fra"}
      aria-pressed={mute}
      onClick={onToggle}
      className="w-11 h-11 rounded-full border-2 border-white/90 bg-ink/60 text-white text-[18px] flex items-center justify-center"
    >
      <SpeakerIcon muted={mute} />
    </button>
  );
}

/**
 * A box's three reels, small, under the slot: spinning, then stopping one
 * after another on what they landed on — all three the same for a jackpot —
 * and gone a moment later.
 */
function Reels({ faces, onDone }: { faces: ItemKind[]; onDone: () => void }) {
  const [stopped, setStopped] = useState(0);
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const roll = TUNING.items.rollTime * 1000;
    const timers = faces.map((_, i) => setTimeout(() => setStopped(i + 1), roll * (0.5 + i * 0.25)));
    timers.push(setTimeout(() => done.current(), roll + 1200));
    return () => timers.forEach(clearTimeout);
  }, [faces]);
  return (
    <div className="flex gap-1.5">
      {faces.map((face, i) => (
        <span key={i} className="relative w-[38px] h-[38px] rounded-full border-2 border-white/80 bg-ink/60 overflow-hidden">
          <span className="absolute left-0 top-0 w-[70px] h-[70px] origin-top-left scale-[0.54]">
            <Roulette item={face} rolling={i >= stopped} />
          </span>
        </span>
      ))}
    </div>
  );
}

/** Debug mode's balance line: the rival's rubber band, how far back you count for items, and your gap to the leader. */
/** Where the player is, in `tracks.ts`'s terms, for the debug readout: a fraction and an offset to paste. */
function where(race: Race | null) {
  const player = race?.karts.find((k) => k.player);
  return race && player ? `${whereText(race.track, player)}\n` : "";
}

function balance(race: Race | null) {
  const player = race?.karts.find((k) => k.player);
  if (!race || !player || race.autopilot) return "";
  const progress = (k: Race["karts"][number]) => k.lap * race.track.n + k.idx;
  const rival = race.karts.find((k) => k.id === race.rival);
  const leader = race.karts.find((k) => k.place === 1)!;
  const band = rival ? rubberBand(race, rival, progress(player) - progress(rival)) : 0;
  return `rival band ${band >= 0 ? "+" : ""}${Math.round(band * 100)}% · back ${backness(race, player).toFixed(2)} · gap ${progress(leader) - progress(player)}u · combo ${race.score.chain}\n`;
}

/** A number counting up to `to`, written straight into the DOM as it goes. */
function CountUp({ to }: { to: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const value = useSpring(0, { stiffness: 60, damping: 20 });
  useEffect(() => {
    value.set(to);
    return value.on("change", (v) => ref.current && (ref.current.textContent = Math.round(v).toLocaleString("da-DK")));
  }, [to, value]);
  return <span ref={ref}>0</span>;
}

const sameItems = (a: ItemKind[], b: ItemKind[]) => a.length === b.length && a.every((k, i) => k === b[i]);

function levels() {
  const sharpest = Math.min(window.devicePixelRatio || 1, 2);
  // A sharp screen never drops below 1.25: at 1x on a 3x phone the picture is a blur.
  const out = RESOLUTIONS.filter((r) => r <= sharpest && (sharpest < 2 || r >= 1.25));
  return out.length ? out : [1];
}

const MINI_PAD = 22;

interface MinimapCache {
  track: Track | null;
  image: HTMLCanvasElement | null;
  x0: number;
  z0: number;
  scale: number;
  faces: Map<string, HTMLCanvasElement>;
}

/** The HUD atlas, loaded once, for the characters' icons on the minimap. */
let hud: HTMLImageElement | null = null;
function iconImage(char: CharKey) {
  const s = sprite("hud", `char-${char}`);
  if (!s) return null;
  if (!hud) {
    hud = new Image();
    hud.src = s.url;
  }
  return { img: hud, rect: s.rect };
}

/**
 * The track from above, with a face per kart — the player's the big one. The
 * track and each emoji are drawn once into canvases of their own; a frame only
 * stamps them, rather than stroking seven hundred points and laying out text.
 */
function drawMinimap(canvas: HTMLCanvasElement | null, race: Race, cache: MinimapCache) {
  const ctx = canvas?.getContext("2d");
  if (!canvas || !ctx || race.autopilot) return;
  const size = canvas.width;
  if (cache.track !== race.track) {
    const { samples } = race.track;
    let [x0, x1, z0, z1] = [Infinity, -Infinity, Infinity, -Infinity];
    for (const s of samples) {
      x0 = Math.min(x0, s.x);
      x1 = Math.max(x1, s.x);
      z0 = Math.min(z0, s.z);
      z1 = Math.max(z1, s.z);
    }
    // Margin enough for a face's ring, even at the very edge of the track.
    Object.assign(cache, { track: race.track, x0, z0, scale: (size - 2 * MINI_PAD) / Math.max(x1 - x0, z1 - z0) });
    const image = (cache.image ??= document.createElement("canvas"));
    image.width = image.height = size;
    const c = image.getContext("2d");
    if (c) {
      const px = (x: number) => MINI_PAD + (x - x0) * cache.scale;
      const pz = (z: number) => MINI_PAD + (z - z0) * cache.scale;
      c.clearRect(0, 0, size, size);
      c.fillStyle = "rgba(70,150,225,0.75)";
      for (const [x, z, r] of race.track.lakes) {
        c.beginPath();
        c.arc(px(x), pz(z), r * cache.scale, 0, Math.PI * 2);
        c.fill();
      }
      c.lineJoin = "round";
      c.beginPath();
      samples.forEach((s, i) => (i ? c.lineTo(px(s.x), pz(s.z)) : c.moveTo(px(s.x), pz(s.z))));
      c.closePath();
      c.strokeStyle = "rgba(26,18,6,0.55)";
      c.lineWidth = 16;
      c.stroke();
      c.strokeStyle = "rgba(255,255,255,0.75)";
      c.lineWidth = 10;
      c.stroke();
      if (isDebugOn()) {
        // Each corner in its drift tier's colour, and the lap's percent every 0.05.
        for (const { from, len, tier } of tierSpans(race.track)) {
          c.beginPath();
          for (let k = 0; k <= len; k++) {
            const s = samples[(from + k) % samples.length];
            if (k) c.lineTo(px(s.x), pz(s.z));
            else c.moveTo(px(s.x), pz(s.z));
          }
          c.strokeStyle = rgbCss(TIER_COLORS[tier]);
          c.lineWidth = 6;
          c.stroke();
        }
        c.font = '700 9px ui-monospace,monospace';
        c.textAlign = "center";
        c.textBaseline = "middle";
        tickSamples(race.track).forEach((i, k) => {
          const s = samples[i];
          c.fillStyle = "#1a1206";
          c.beginPath();
          c.arc(px(s.x), pz(s.z), 2.5, 0, Math.PI * 2);
          c.fill();
          c.fillText(String(Math.round(k * TICK_STEP * 100)), px(s.x) + s.nx * 13, pz(s.z) + s.nz * 13);
        });
      }
      c.fillStyle = "#e3342f";
      c.fillRect(px(samples[0].x) - 3, pz(samples[0].z) - 9, 6, 18);
    }
  }
  // A dot in the driver's kart colour with their character's head in it (their
  // initial until the icon's in), the player's gold and bigger, the rival's ringed in red.
  const face = (k: Race["karts"][number], rival: boolean) => {
    const key = `${k.player ? "P" : rival ? "R" : ""}${k.id}`;
    let img = cache.faces.get(key);
    const char = characterOf(k);
    const icon = char ? iconImage(char) : null;
    const ready = !icon || (icon.img.complete && icon.img.naturalWidth > 0);
    if (!img) {
      img = document.createElement("canvas");
      img.width = img.height = 44;
      const c = img.getContext("2d");
      if (c) {
        c.beginPath();
        c.arc(22, 22, k.player ? 20 : 18, 0, Math.PI * 2);
        c.fillStyle = k.player ? "#ffd23a" : rgbCss(kartStyle(k).body);
        c.fill();
        if (icon && ready) {
          c.save();
          c.clip();
          const [sx, sy, sw, sh] = icon.rect;
          c.drawImage(icon.img, sx, sy, sw, sh, 1, 0, 42, 42);
          c.restore();
        } else {
          c.fillStyle = k.player ? "#1a1206" : "#fff";
          c.font = `800 ${k.player ? 24 : 20}px "Bricolage Grotesque",Archivo,Inter,sans-serif`;
          c.textAlign = "center";
          c.textBaseline = "middle";
          c.fillText(Array.from(k.name.trim())[0]?.toUpperCase() ?? "?", 22, 23);
        }
        // Ringed in their kart's colour (yours gold, the rival's red), edged dark.
        c.beginPath();
        c.arc(22, 22, k.player ? 19 : 17, 0, Math.PI * 2);
        c.lineWidth = 4;
        c.strokeStyle = k.player ? "#ffd23a" : rival ? "#e3342f" : rgbCss(kartStyle(k).body);
        c.stroke();
        c.beginPath();
        c.arc(22, 22, k.player ? 21.2 : 19.2, 0, Math.PI * 2);
        c.lineWidth = 1.5;
        c.strokeStyle = "#1a1206";
        c.stroke();
      }
      // Kept once it's final: with the icon, or with no icon to wait for.
      if (ready) cache.faces.set(key, img);
    }
    return img;
  };
  ctx.clearRect(0, 0, size, size);
  if (cache.image) ctx.drawImage(cache.image, 0, 0);
  // The player last, on top.
  for (const pass of [false, true]) {
    for (const k of race.karts) {
      if (k.player !== pass) continue;
      ctx.drawImage(face(k, k.id === race.rival), MINI_PAD + (k.x - cache.x0) * cache.scale - 22, MINI_PAD + (k.z - cache.z0) * cache.scale - 22);
    }
  }
}

